import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  XMarkIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import Pagination from '../components/Pagination';
import ExportButton from '../components/ExportButton';
import PermissionButton from '../components/PermissionButton';
import { toast } from 'react-toastify';
import { usePermission } from '../hooks/useAuth';
import {
  useGetCashTransactionsQuery,
  useAddCashTransactionMutation,
  useDeleteCashTransactionMutation,
  useGetTransactionCategoriesQuery,
  useAddTransactionCategoryMutation,
  useUpdateTransactionCategoryMutation,
  useDeleteTransactionCategoryMutation,

  useGetConsultantsQuery,
  useGetActiveContractsQuery,
  useGetActiveStudentsQuery,
  useGetCurrenciesQuery,
  useGetCashTransactionPaymentMethodsQuery,
  useAddPaymentMethodMutation,
  useUpdatePaymentMethodMutation,
  useDeletePaymentMethodMutation
} from '../store/api';

export default function CashManagement() {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const [viewingTransaction, setViewingTransaction] = useState(null);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [isPaymentMethodModalOpen, setIsPaymentMethodModalOpen] = useState(false);
  const [editingPaymentMethod, setEditingPaymentMethod] = useState(null);
  const [isExport, setIsExport] = useState(0);
  
  // Permission hooks - strictly permission-based
  const canAddTransaction = usePermission('cash_management.add');
  const canDeleteTransaction = usePermission('cash_management.delete');
  const canViewCashManagement = usePermission('cash_management.view'); // Already being used in route protection
  const canManagePaymentCategories = usePermission('payment_category.management');
  const canManagePaymentMethods = usePermission('payment_method.management');
  
  // Payment workflow states
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [selectedContractId, setSelectedContractId] = useState('');
  const [selectedPaymentScheduleIds, setSelectedPaymentScheduleIds] = useState([]);
  const [paymentFormData, setPaymentFormData] = useState({
    amount: '',
    currency_id: '',
    payment_method_id: '',
    description: '',
    notes: '',
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5)
  });
  
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    color: '#3B82F6',
    type: 'income'
  });

  const [paymentMethodFormData, setPaymentMethodFormData] = useState({
    name: '',
    code: '',
    description: '',
    is_active: true
  });
  const [categoryTab, setCategoryTab] = useState('income');
  
  const itemsPerPage = 10;

  // API Hooks
  const { 
    data: transactionsData, 
    isLoading: transactionsLoading, 
    refetch: refetchTransactions 
  } = useGetCashTransactionsQuery({
    page: currentPage,
    per_page: itemsPerPage,
    search: searchTerm,
    category_type: typeFilter,
    type_id: categoryFilter,
    date_from: dateFromFilter,
    date_to: dateToFilter
  });

  const { 
    data: transactionCategoriesData, 
    isLoading: categoriesLoading 
  } = useGetTransactionCategoriesQuery();
  const { data: consultantsData } = useGetConsultantsQuery();
  const { data: contractsData } = useGetActiveContractsQuery();
  const { data: studentsData } = useGetActiveStudentsQuery();
  const { data: currenciesData } = useGetCurrenciesQuery();
  const { data: paymentMethodsData } = useGetCashTransactionPaymentMethodsQuery();

  // Ensure we always have arrays to work with - more robust checking
  const transactionCategories = React.useMemo(() => {
    if (!transactionCategoriesData) return [];
    
    // Handle different possible response structures
    let categories = [];
    if (transactionCategoriesData.data?.data && Array.isArray(transactionCategoriesData.data.data)) {
      categories = transactionCategoriesData.data.data;
    } else if (transactionCategoriesData.data && Array.isArray(transactionCategoriesData.data)) {
      categories = transactionCategoriesData.data;
    } else if (Array.isArray(transactionCategoriesData)) {
      categories = transactionCategoriesData;
    }
    
    return categories;
  }, [transactionCategoriesData]);

  // Use transaction categories as transaction types for type_id field
  const transactionTypes = transactionCategories;
  const consultants = Array.isArray(consultantsData?.data) ? consultantsData.data : [];
  const contracts = Array.isArray(contractsData?.data) ? contractsData.data : [];
  const students = Array.isArray(studentsData?.data) ? studentsData.data : [];
  const currencies = Array.isArray(currenciesData?.data) ? currenciesData.data : [];
  const paymentMethods = Array.isArray(paymentMethodsData?.data) ? paymentMethodsData.data : [];

  // Get selected student data
  const selectedStudent = students.find(student => student.id.toString() === selectedStudentId);
  const studentContracts = selectedStudent?.contracts || [];
  
  // Get unpaid contracts (status_id !== 4 means not fully paid)
  const unpaidContracts = studentContracts.filter(contract => contract.status_id !== 4);
  
  // Get selected contract data
  const selectedContract = studentContracts.find(contract => contract.id.toString() === selectedContractId);
  const paymentSchedules = selectedContract?.payment_schedule || [];
  
  // Get all payment schedules (we'll handle paid/unpaid status in the UI)
  const allPaymentSchedules = paymentSchedules;

  // Helper function to safely get consultant name
  const getConsultantName = (consultant) => {
    if (!consultant) return 'Sistem';
    if (typeof consultant === 'string') return consultant;
    if (typeof consultant === 'object') {
      // Handle processed_by object structure
      if (consultant.admin) {
        return `${consultant.admin.first_name} ${consultant.admin.last_name}`;
      }
      return consultant.name || consultant.admin?.name || `Konsultant ${consultant.id}`;
    }
    return 'Sistem';
  };

  // Helper function to get currency name by ID
  const getCurrencyName = (currencyId) => {
    const currency = currencies.find(c => c.id === currencyId);
    return currency?.code || currency?.name || 'AZN';
  };

  // Helper function to get payment method name by ID
  const getPaymentMethodName = (paymentMethodId) => {
    const method = paymentMethods.find(m => m.id === paymentMethodId);
    return method?.name || 'N/A';
  };

  // Helper function to get contract number by transaction
  const getContractNumber = (transaction) => {
    // First check if contract object exists
    if (transaction.contract?.contract_number) {
      return transaction.contract.contract_number;
    }
    
    // If no contract object but contract_id exists, try to find from contracts list
    if (transaction.contract_id && contracts.length > 0) {
      const contract = contracts.find(c => c.id === transaction.contract_id);
      return contract?.contract_number || `Müqavilə #${transaction.contract_id}`;
    }
    
    return 'N/A';
  };

  // Helper function to format date from ISO to readable format
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('az-AZ', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Helper function to format datetime from ISO to readable format
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('az-AZ', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Check if payment schedule is overdue
  const isOverdue = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    return due < today;
  };

  // Check if payment schedule is due soon (within 7 days)
  const isDueSoon = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 7;
  };

  // Handle student selection
  const handleStudentChange = (studentId) => {
    setSelectedStudentId(studentId);
    setSelectedContractId('');
    setSelectedPaymentScheduleIds([]);
    setPaymentFormData({
      amount: '',
      currency_id: '',
      payment_method_id: '',
      description: '',
      notes: '',
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().slice(0, 5)
    });
  };

  // Handle contract selection
  const handleContractChange = (contractId) => {
    setSelectedContractId(contractId);
    setSelectedPaymentScheduleIds([]);
    setPaymentFormData({
      amount: '',
      currency_id: '',
      payment_method_id: '',
      description: '',
      notes: '',
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().slice(0, 5)
    });
  };

  // Handle payment schedule selection (only allow unpaid schedules)
  const handlePaymentScheduleToggle = (scheduleId) => {
    const schedule = allPaymentSchedules.find(s => s.id === scheduleId);
    
    // Don't allow selection of paid schedules
    if (parseFloat(schedule?.paid_amount || 0) > 0) {
      return;
    }
    
    setSelectedPaymentScheduleIds(prev => {
      if (prev.includes(scheduleId)) {
        return prev.filter(id => id !== scheduleId);
      } else {
        return [...prev, scheduleId];
      }
    });
  };

  // Calculate total amount for selected payment schedules (only unpaid ones)
  const calculateTotalAmount = () => {
    return allPaymentSchedules
      .filter(schedule => 
        selectedPaymentScheduleIds.includes(schedule.id) && 
        parseFloat(schedule.paid_amount || 0) === 0
      )
      .reduce((sum, schedule) => sum + parseFloat(schedule.amount || 0), 0);
  };

  // Handle payment submission
  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedPaymentScheduleIds.length === 0) {
      toast.error('Heç olmasa bir ödəniş cədvəli seçin!');
      return;
    }

    if (!paymentFormData.amount || !paymentFormData.currency_id || !paymentFormData.payment_method_id) {
      toast.error('Zəhmət olmasa bütün məcburi sahələri doldurun!');
      return;
    }

    const totalAmount = calculateTotalAmount();
    if (parseFloat(paymentFormData.amount) !== totalAmount) {
      toast.error(`Ödəniş məbləği seçilmiş cədvəllərin cəmi ilə uyğun gəlməlidir: ${totalAmount.toLocaleString()}`);
      return;
    }

    try {
      const paymentData = {
        payment_schedule_ids: selectedPaymentScheduleIds,
        amount: parseFloat(paymentFormData.amount),
        currency_id: paymentFormData.currency_id,
        payment_method_id: paymentFormData.payment_method_id,
        description: paymentFormData.description,
        notes: paymentFormData.notes,
        date: paymentFormData.date,
        time: paymentFormData.time,
        student_id: selectedStudentId,
        contract_id: selectedContractId
      };

      // For now, we'll use addTransaction since payPaymentSchedules endpoint might not exist yet
      const transactionData = {
        transaction_number: generateTransactionNumber(),
        date: paymentFormData.date,
        time: paymentFormData.time,
        type_id: '', // Will need to be set based on income category
        category_type: 'income',
        amount: parseFloat(paymentFormData.amount),
        currency_id: paymentFormData.currency_id,
        description: paymentFormData.description || `Tələbə ödənişi - ${selectedContract?.contract_number}`,
        contract_id: selectedContractId,
        student_id: selectedStudentId,
        payment_method_id: paymentFormData.payment_method_id,
        notes: paymentFormData.notes,
        payment_schedule_ids: selectedPaymentScheduleIds, // Send selected schedule IDs to backend
        is_active: true
      };

      await addTransaction(transactionData).unwrap();
      toast.success('Ödəniş uğurla həyata keçirildi!');
      
      // Reset form
      setSelectedPaymentScheduleIds([]);
      setPaymentFormData({
        amount: '',
        currency_id: '',
        payment_method_id: '',
        description: '',
        notes: '',
        date: new Date().toISOString().split('T')[0],
        time: new Date().toTimeString().slice(0, 5)
      });
      refetchTransactions();
    } catch (error) {
      if (error?.data?.errors) {
        Object.entries(error.data.errors).forEach(([field, messages]) => {
          messages.forEach(message => {
            toast.error(`${field}: ${message}`);
          });
        });
      } else if (error?.data?.message) {
        toast.error(error.data.message);
      } else {
        toast.error('Xəta baş verdi!');
      }
    }
  };

  // Update total amount when payment schedules change
  useEffect(() => {
    if (selectedPaymentScheduleIds.length > 0) {
      const total = calculateTotalAmount();
      setPaymentFormData(prev => ({
        ...prev,
        amount: total.toString()
      }));
    } else {
      setPaymentFormData(prev => ({
        ...prev,
        amount: ''
      }));
    }
  }, [selectedPaymentScheduleIds, allPaymentSchedules]);

  // Reset category filter when type filter changes
  useEffect(() => {
    setCategoryFilter('');
  }, [typeFilter]);

  const [addTransaction] = useAddCashTransactionMutation();
  const [deleteTransaction] = useDeleteCashTransactionMutation();
  const [addCategory] = useAddTransactionCategoryMutation();
  const [updateCategory] = useUpdateTransactionCategoryMutation();
  const [deleteCategory] = useDeleteTransactionCategoryMutation();

  const [addPaymentMethod] = useAddPaymentMethodMutation();
  const [updatePaymentMethod] = useUpdatePaymentMethodMutation();
  const [deletePaymentMethod] = useDeletePaymentMethodMutation();

  const [formData, setFormData] = useState({
    transaction_number: '',
    date: new Date().toISOString().split('T')[0],
    time: '',
    type_id: '',
    amount: '',
    currency_id: '',
    description: '',
    contract_id: '',
    student_id: '',
    payment_method_id: '',
    category_type: '',
    reference: '',
    processed_by: '',
    processed_by_id: '',
    notes: '',
    is_active: true
  });

  // Generate transaction number
  const generateTransactionNumber = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const timestamp = Date.now().toString().slice(-6);
    return `TXN${year}${month}${day}${timestamp}`;
  };



  const transactions = transactionsData?.data || [];
  const totalPages = Math.ceil((transactionsData?.total || 0) / itemsPerPage);

  // Calculate summary statistics
  const totalIncome = transactions
    .filter(t => t.category_type === 'income')
    .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
  
  const totalExpense = transactions
    .filter(t => t.category_type === 'expense')
    .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
  
  const netProfit = totalIncome - totalExpense;
  const transactionCount = transactions.length;

  const handleAdd = () => {
    setFormData({
      transaction_number: generateTransactionNumber(),
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().slice(0, 5),
      type_id: '',
      category_type: '',
      amount: '',
      currency_id: '',
      description: '',
      contract_id: '',
      student_id: '',
      payment_method_id: '',
      reference: '',
      processed_by: '',
      processed_by_id: '',
      notes: '',
      is_active: true
    });
    
    // Reset payment schedule states
    setSelectedStudentId('');
    setSelectedContractId('');
    setSelectedPaymentScheduleIds([]);
    
    setIsModalOpen(true);
  };



  const handleView = (transaction) => {
    setViewingTransaction(transaction);
    setIsViewModalOpen(true);
  };

  const handleValidationErrors = (error) => {
    if (error?.data?.errors) {
      Object.entries(error.data.errors).forEach(([field, messages]) => {
        messages.forEach(message => {
          toast.error(`${field}: ${message}`);
        });
      });
    } else if (error?.data?.message) {
      toast.error(error.data.message);
    } else {
      toast.error('Xəta baş verdi!');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if this is a payment schedule transaction
    const isPaymentScheduleTransaction = selectedPaymentScheduleIds.length > 0;
    
    if (isPaymentScheduleTransaction) {
      // Payment schedule transaction validation
      if (!selectedStudentId || !selectedContractId || !formData.payment_method_id || !formData.currency_id || !formData.type_id) {
        toast.error('Zəhmət olmasa tələbə, müqavilə, kateqoriya, ödəniş üsulu və valyutanı seçin!');
        return;
      }
      
      if (selectedPaymentScheduleIds.length === 0) {
        toast.error('Heç olmasa bir ödəniş cədvəli seçin!');
        return;
      }
    } else {
      // Regular transaction validation
      if (!formData.transaction_number || !formData.date || !formData.amount || !formData.category_type || !formData.type_id || !formData.currency_id || !formData.payment_method_id) {
        toast.error('Zəhmət olmasa bütün məcburi sahələri doldurun!');
        return;
      }
      
      if (parseFloat(formData.amount) <= 0) {
        toast.error('Məbləğ 0-dan böyük olmalıdır!');
        return;
      }
    }
    
    try {
      let transactionData;
      
      if (isPaymentScheduleTransaction) {
        // Payment schedule transaction
        const totalAmount = calculateTotalAmount();
        transactionData = {
          transaction_number: generateTransactionNumber(),
          date: formData.date,
          time: formData.time,
          type_id: formData.type_id, // Include category ID from formData
          category_type: 'income', // Payment schedules are always income
          amount: totalAmount,
          currency_id: formData.currency_id,
          description: formData.description || `Tələbə ödənişi - ${selectedContract?.contract_number}`,
          contract_id: selectedContractId,
          student_id: selectedStudentId,
          payment_method_id: formData.payment_method_id,
          notes: formData.notes,
          payment_schedule_ids: selectedPaymentScheduleIds, // Send selected schedule IDs to backend
          processed_by_id: formData.processed_by_id || null,
          reference: formData.reference || null,
          is_active: true
        };
      } else {
        // Regular transaction
        transactionData = {
          ...formData,
          amount: parseFloat(formData.amount),
          contract_id: selectedContractId || null,
          student_id: selectedStudentId || null,
          processed_by_id: formData.processed_by_id || null,
          currency_id: formData.currency_id || null,
          payment_method_id: formData.payment_method_id || null
        };
      }

      await addTransaction(transactionData).unwrap();
      toast.success('Əməliyyat uğurla əlavə edildi!');
    
      setIsModalOpen(false);
      
      // Reset payment schedule states
      if (isPaymentScheduleTransaction) {
        setSelectedStudentId('');
        setSelectedContractId('');
        setSelectedPaymentScheduleIds([]);
      }
      
      refetchTransactions();
    } catch (error) {
      handleValidationErrors(error);
    }
  };

  const handleDelete = async (transaction) => {
    if (window.confirm(`"${transaction.description}" əməliyyatını silmək istədiyinizə əminsiniz?\n\nBu əməliyyat geri alına bilməz.`)) {
      try {
        await deleteTransaction(transaction.id).unwrap();
        toast.success('Əməliyyat uğurla silindi!');
        refetchTransactions();
      } catch (error) {
        handleValidationErrors(error);
      }
    }
  };

  // Category management functions
  const handleAddCategory = () => {
    setEditingCategory(null);
    setCategoryFormData({
      name: '',
      color: '#3B82F6',
      type: categoryTab
    });
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name,
      color: category.color || '#3B82F6',
      type: category.type
    });
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    
    if (!categoryFormData.name.trim()) {
      toast.error('Kateqoriya adı məcburidir!');
      return;
    }

    try {
    const categoryData = {
      name: categoryFormData.name.trim(),
        color: categoryFormData.color,
      type: categoryFormData.type
    };

      if (editingCategory) {
        await updateCategory({ 
          id: editingCategory.id, 
          obj: categoryData 
        }).unwrap();
        toast.success('Kateqoriya uğurla yeniləndi!');
      } else {
        await addCategory(categoryData).unwrap();
        toast.success('Kateqoriya uğurla əlavə edildi!');
      }
    
    // Reset form
    setCategoryFormData({
      name: '',
        color: '#3B82F6',
      type: categoryTab
    });
    setEditingCategory(null);
    } catch (error) {
      handleValidationErrors(error);
    }
  };

  const handleDeleteCategory = async (category) => {
    if (window.confirm(`"${category.name}" kateqoriyasını silmək istədiyinizə əminsiniz?\n\nBu əməliyyat geri alına bilməz.`)) {
      try {
        await deleteCategory(category.id).unwrap();
        toast.success('Kateqoriya uğurla silindi!');
      } catch (error) {
        handleValidationErrors(error);
      }
    }
  };

  // Payment Method management functions
  const handleAddPaymentMethod = () => {
    setEditingPaymentMethod(null);
    setPaymentMethodFormData({
      name: '',
      code: '',
      description: '',
      is_active: true
    });
  };

  const handleEditPaymentMethod = (paymentMethod) => {
    setEditingPaymentMethod(paymentMethod);
    setPaymentMethodFormData({
      name: paymentMethod.name,
      code: paymentMethod.code,
      description: paymentMethod.description || '',
      is_active: paymentMethod.is_active !== false
    });
  };

  const handlePaymentMethodSubmit = async (e) => {
    e.preventDefault();
    
    if (!paymentMethodFormData.name.trim()) {
      toast.error('Ödəniş üsulu adı məcburidir!');
      return;
    }

    if (!paymentMethodFormData.code.trim()) {
      toast.error('Ödəniş üsulu kodu məcburidir!');
      return;
    }

    // Validate code format (uppercase letters and underscores only)
    if (!/^[A-Z_]+$/.test(paymentMethodFormData.code.trim())) {
      toast.error('Kod yalnız böyük hərflər və alt xətt (_) saxlaya bilər!');
      return;
    }

    try {
      const paymentMethodData = {
        name: paymentMethodFormData.name.trim(),
        code: paymentMethodFormData.code.trim(),
        description: paymentMethodFormData.description.trim() || null,
        is_active: paymentMethodFormData.is_active
      };

      if (editingPaymentMethod) {
        await updatePaymentMethod({ 
          id: editingPaymentMethod.id, 
          obj: paymentMethodData 
        }).unwrap();
        toast.success('Ödəniş üsulu uğurla yeniləndi!');
      } else {
        await addPaymentMethod(paymentMethodData).unwrap();
        toast.success('Ödəniş üsulu uğurla əlavə edildi!');
      }
    
      // Reset form
      setPaymentMethodFormData({
        name: '',
        code: '',
        description: '',
        is_active: true
      });
      setEditingPaymentMethod(null);
    } catch (error) {
      handleValidationErrors(error);
    }
  };

  const handleDeletePaymentMethod = async (paymentMethod) => {
    if (window.confirm(`"${paymentMethod.name}" ödəniş üsulunu silmək istədiyinizə əminsiniz?\n\nBu əməliyyat geri alına bilməz.`)) {
      try {
        await deletePaymentMethod(paymentMethod.id).unwrap();
        toast.success('Ödəniş üsulu uğurla silindi!');
      } catch (error) {
        handleValidationErrors(error);
      }
    }
  };

  // Handle category type change to filter categories
  const handleCategoryTypeChange = (categoryType) => {
    setFormData({
      ...formData, 
      category_type: categoryType,
      type_id: '' // Reset selected category when type changes
    });
    
    // Reset payment schedule states when changing category type
    if (categoryType !== 'income') {
      setSelectedStudentId('');
      setSelectedContractId('');
      setSelectedPaymentScheduleIds([]);
    }
  };

  // Handle type change (category selection)
  const handleTypeChange = (typeId) => {
    setFormData({
      ...formData, 
      type_id: typeId
    });
  };

  // Get filtered categories based on selected category type
  const getFilteredCategories = () => {
    if (!formData.category_type) return transactionTypes;
    return transactionTypes.filter(category => category.type === formData.category_type);
  };

  // Export data transformation
  const getExportData = () => {
    return transactions.map(transaction => ({
      transaction_number: transaction.transaction_number,
      date: formatDate(transaction.date),
      time: transaction.time || 'N/A',
      type: transaction.category_type === 'income' ? 'Gəlir' : 'Xərc',
      category: transaction.transaction_type?.name || 'N/A',
      description: transaction.description,
      amount: transaction.amount,
      currency: getCurrencyName(transaction.currency_id),
      payment_method: getPaymentMethodName(transaction.payment_method_id),
      consultant: transaction.user?.name || 
                  transaction.consultant?.admin?.name || 
                  getConsultantName(transaction.processed_by) || 
                  getConsultantName(transaction.consultant) || 
                  'Sistem',
      contract: getContractNumber(transaction),
      student: transaction.student ? `${transaction.student.first_name} ${transaction.student.last_name}` : 'N/A',
      reference: transaction.reference || 'N/A',
      notes: transaction.notes || 'N/A'
    }));
  };

  if (transactionsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kassa İdarəetməsi</h1>
                      <p className="mt-1 text-sm text-gray-600">
              Gəlir və xərcləri idarə edin, maliyyə hesabatlarını izləyin
            </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          {canManagePaymentCategories && (
            <button
              onClick={() => setIsCategoryModalOpen(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <CurrencyDollarIcon className="-ml-1 mr-2 h-5 w-5" />
              Kateqoriyalar
            </button>
          )}
          {canManagePaymentMethods && (
            <button
              onClick={() => setIsPaymentMethodModalOpen(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <BanknotesIcon className="-ml-1 mr-2 h-5 w-5" />
              Ödəniş Üsulları
            </button>
          )}
          <ExportButton 
            data={getExportData()}
            filename="kassa-emeliyyatlari"
            columns={[
              { key: 'transaction_number', label: 'Əməliyyat №' },
              { key: 'date', label: 'Tarix' },
              { key: 'time', label: 'Vaxt' },
              { key: 'type', label: 'Növ' },
              { key: 'category', label: 'Kateqoriya' },
              { key: 'description', label: 'Təsvir' },
              { key: 'amount', label: 'Məbləğ' },
              { key: 'currency', label: 'Valyuta' },
              { key: 'payment_method', label: 'Ödəniş üsulu' },
              { key: 'consultant', label: 'Konsultant' },
              { key: 'reference', label: 'Referans' }
            ]}
            setIsExport={setIsExport}
          />
          {/* Add Transaction button - only show if user has cash_management.add permission */}
          <PermissionButton 
            onClick={handleAdd}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            permission="cash_management.add"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Yeni Əməliyyat
          </PermissionButton>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ArrowTrendingUpIcon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className="text-green-100 text-sm font-medium">Ümumi Gəlir</p>
              <p className="text-2xl font-bold">{totalIncome.toLocaleString()} AZN</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ArrowTrendingDownIcon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className="text-red-100 text-sm font-medium">Ümumi Xərc</p>
              <p className="text-2xl font-bold">{totalExpense.toLocaleString()} AZN</p>
            </div>
          </div>
        </div>

        <div className={`bg-gradient-to-r ${netProfit >= 0 ? 'from-blue-500 to-blue-600' : 'from-orange-500 to-orange-600'} rounded-lg shadow-lg p-6 text-white`}>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BanknotesIcon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className={`${netProfit >= 0 ? 'text-blue-100' : 'text-orange-100'} text-sm font-medium`}>Xalis Mənfəət</p>
              <p className="text-2xl font-bold">{netProfit.toLocaleString()} AZN</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CurrencyDollarIcon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className="text-purple-100 text-sm font-medium">Əməliyyat Sayı</p>
              <p className="text-2xl font-bold">{transactionCount}</p>
            </div>
          </div>
        </div>
      </div>



      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-200">
        <div className="mb-4">
          <h3 className="text-md font-medium text-gray-900 mb-2">Filterlər</h3>
          <p className="text-sm text-gray-600">
            Əməliyyatları axtarmaq və filtreləmək üçün aşağıdakı sahələri istifadə edin
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="relative">
            <label className="block text-xs font-medium text-gray-700 mb-1">Axtarış</label>
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" style={{top: '20px'}}>
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Təsvir, referans..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Növ</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Bütün növlər</option>
              <option value="income">Gəlir</option>
              <option value="expense">Xərc</option>
            </select>
          </div>

                    <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Kateqoriya</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
              disabled={categoriesLoading || !typeFilter}
            >
              <option value="">
                {!typeFilter ? 'Əvvəlcə növ seçin' : 'Bütün kateqoriyalar'}
              </option>
              {categoriesLoading ? (
                <option disabled>Yüklənir...</option>
              ) : (
                transactionCategories
                  .filter(category => !typeFilter || category.type === typeFilter)
                  .map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))
              )}
            </select>
          </div>

          <div className="relative">
            <label className="block text-xs font-medium text-gray-700 mb-1">Başlanğıc tarixi</label>
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" style={{top: '20px'}}>
              <CalendarDaysIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="date"
              value={dateFromFilter}
              onChange={(e) => setDateFromFilter(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="relative">
            <label className="block text-xs font-medium text-gray-700 mb-1">Bitmə tarixi</label>
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" style={{top: '20px'}}>
              <CalendarDaysIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="date"
              value={dateToFilter}
              onChange={(e) => setDateToFilter(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setTypeFilter('');
                setCategoryFilter('');
                setDateFromFilter('');
                setDateToFilter('');
                setCurrentPage(1);
              }}
              className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Təmizlə
            </button>
          </div>
        </div>
      </div>

      {/* Transactions table */}
      <div className="bg-white shadow-lg overflow-hidden sm:rounded-xl border border-blue-100">
        <div className="px-6 py-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-blue-50 to-blue-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Əməliyyat №
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Tarix
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Əməliyyat
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Kateqoriya
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Məbləğ
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Konsultant
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Əməliyyatlar
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {transactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 transition-all duration-200 group">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900 group-hover:text-blue-900 transition-colors">
                        {transaction.transaction_number}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-gray-500 to-gray-600 flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow mr-3">
                          <CalendarDaysIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-900 group-hover:text-blue-900 transition-colors">
                            {formatDate(transaction.date)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {transaction.time || 'N/A'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="font-medium">{transaction.description}</div>
                        <div className="text-xs text-gray-500">{transaction.reference}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          transaction.category_type === 'income' 
                            ? 'bg-green-100 text-green-800 border border-green-200' 
                            : 'bg-red-100 text-red-800 border border-red-200'
                        }`}>
                          {transaction.transaction_type?.color && (
                            <div 
                              className="w-2 h-2 rounded-full mr-1.5 border border-gray-300" 
                              style={{backgroundColor: transaction.transaction_type.color}}
                            ></div>
                          )}
                          {transaction.category_type === 'income' ? (
                            <ArrowTrendingUpIcon className="h-3 w-3 mr-1" />
                          ) : (
                            <ArrowTrendingDownIcon className="h-3 w-3 mr-1" />
                          )}
                          {transaction.transaction_type?.name || 'N/A'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm font-semibold ${
                        transaction.category_type === 'income' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {transaction.category_type === 'income' ? '+' : '-'}{parseFloat(transaction.amount || 0).toLocaleString()} {getCurrencyName(transaction.currency_id)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {transaction.user?.name || 
                         transaction.consultant?.admin?.name || 
                         getConsultantName(transaction.processed_by) || 
                         getConsultantName(transaction.consultant) || 
                         'Sistem'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end space-x-2">
                                                  {/* View button - always visible if user can view cash management (already protected by route) */}
                          <button
                            onClick={() => handleView(transaction)}
                            className="inline-flex items-center p-2 bg-green-100 text-green-600 hover:bg-green-200 hover:text-green-800 rounded-lg transition-all duration-200 hover:scale-105"
                            title="Ətraflı bax"
                          >
                            <EyeIcon className="h-4 w-4" />
                                                    </button>
                          
                          {/* Delete button - only show if user has cash_management.delete permission */}
                          <PermissionButton 
                            onClick={() => handleDelete(transaction)}
                            className="inline-flex items-center p-2 bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-800 rounded-lg transition-all duration-200 hover:scale-105"
                            permission="cash_management.delete"
                            title="Sil"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </PermissionButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {transactions.length === 0 && (
            <div className="text-center py-16">
              <div className="mx-auto h-16 w-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mb-4">
                <BanknotesIcon className="h-8 w-8 text-blue-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Heç bir əməliyyat tapılmadı</h3>
              <p className="text-sm text-gray-500 max-w-sm mx-auto">
                Axtarış şərtlərinizi dəyişin və ya yeni əməliyyat əlavə edin.
              </p>
                              {/* Add Transaction button in empty state - only show if user has cash_management.add permission */}
                <PermissionButton 
                  onClick={handleAdd}
                  className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  permission="cash_management.add"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  İlk Əməliyyatı Əlavə Et
                </PermissionButton>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        totalItems={transactionsData?.total || 0}
        itemsPerPage={itemsPerPage}
      />

      {/* Add/Edit Transaction Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Yeni Əməliyyat Əlavə Et
                </h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Transaction Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Əməliyyat Nömrəsi *
                    </label>
                    <input
                      type="text"
                      value={formData.transaction_number}
                      onChange={(e) => setFormData({...formData, transaction_number: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                      placeholder="TXN202412310001"
                    />
                  </div>

                  {/* Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tarix *
                    </label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({...formData, date: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  {/* Time */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vaxt
                    </label>
                    <input
                      type="time"
                      value={formData.time}
                      onChange={(e) => setFormData({...formData, time: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Category Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Əməliyyat Növü *
                    </label>
                    <select
                      value={formData.category_type}
                      onChange={(e) => handleCategoryTypeChange(e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Növ seçin</option>
                      <option value="income">Gəlir</option>
                      <option value="expense">Xərc</option>
                    </select>
                  </div>

                  {/* Transaction Category */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Kateqoriya *
                    </label>
                    <select
                      value={formData.type_id}
                      onChange={(e) => handleTypeChange(e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                      disabled={!formData.category_type}
                    >
                      <option value="">Kateqoriya seçin</option>
                      {getFilteredCategories().map(type => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Student Selection - Only show if category_type is income */}
                  {formData.category_type === 'income' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tələbə
                      </label>
                      <select
                        value={selectedStudentId}
                        onChange={(e) => handleStudentChange(e.target.value)}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Tələbə seçin</option>
                        {students.map(student => (
                          <option key={student.id} value={student.id}>
                            {student.first_name} {student.last_name} - {student.phone}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Contract Selection - Only show if category_type is income and student is selected */}
                  {formData.category_type === 'income' && selectedStudentId && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Müqavilə
                      </label>
                      <select
                        value={selectedContractId}
                        onChange={(e) => handleContractChange(e.target.value)}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Müqavilə seçin</option>
                        {unpaidContracts.map(contract => (
                          <option key={contract.id} value={contract.id}>
                            {contract.contract_number} - {contract.total_amount} {contract.currency}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Payment Method */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ödəniş Üsulu *
                    </label>
                    <select
                      value={formData.payment_method_id}
                      onChange={(e) => setFormData({...formData, payment_method_id: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Ödəniş üsulu seçin</option>
                      {paymentMethods.map(method => (
                        <option key={method.id} value={method.id}>
                          {method.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Currency */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Valyuta *
                    </label>
                    <select
                      value={formData.currency_id}
                      onChange={(e) => setFormData({...formData, currency_id: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Valyuta seçin</option>
                      {currencies.map(currency => (
                        <option key={currency.id} value={currency.id}>
                          {currency.code} - {currency.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Consultant */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Konsultant
                    </label>
                    <select
                      value={formData.processed_by_id}
                      onChange={(e) => setFormData({...formData, processed_by_id: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Konsultant seçin</option>
                      {(consultants || []).filter(c => c && c.is_active).map(consultant => (
                        <option key={consultant.id} value={consultant.id}>
                          {getConsultantName(consultant)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Reference */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Referans
                    </label>
                    <input
                      type="text"
                      value={formData.reference}
                      onChange={(e) => setFormData({...formData, reference: e.target.value})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Referans nömrəsi..."
                    />
                  </div>
                </div>

                {/* Payment Schedules Section - Only show for income transactions */}
                {formData.category_type === 'income' && selectedContractId && allPaymentSchedules.length > 0 && (
                  <div className="border-t pt-6">
                    <div className="mb-4">
                      <h4 className="text-md font-medium text-gray-900 mb-2">
                        Ödəniş Cədvəli
                      </h4>
                      <p className="text-sm text-gray-600">
                        Ödənəcək ödəniş cədvəllərini seçin. Artıq ödənilmiş cədvəllər yaşıl işarə ilə göstərilir və seçilə bilməz.
                      </p>
                    </div>

                    {allPaymentSchedules.filter(s => parseFloat(s.paid_amount || 0) === 0).length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <div className="mx-auto h-12 w-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                          <CheckIcon className="h-6 w-6 text-green-600" />
                        </div>
                        <p className="text-sm font-medium">Bu müqavilənin bütün ödənişləri tamamlanmışdır</p>
                        <p className="text-xs text-gray-400 mt-1">Ödənəcək ödəniş cədvəli qalmamışdır</p>
                      </div>
                    ) : (
                      <div className="space-y-3 mb-6">
                      {allPaymentSchedules.map((schedule) => {
                        const isSelected = selectedPaymentScheduleIds.includes(schedule.id);
                        const overdue = isOverdue(schedule.due_date);
                        const dueSoon = isDueSoon(schedule.due_date);
                        const isPaid = parseFloat(schedule.paid_amount || 0) > 0;
                        const isDisabled = isPaid;
                        
                        return (
                          <div
                            key={schedule.id}
                            className={`border rounded-lg p-4 transition-all ${
                              isDisabled
                                ? 'border-gray-300 bg-gray-100 opacity-75 cursor-not-allowed'
                                : isSelected 
                                  ? 'border-blue-500 bg-blue-50 cursor-pointer' 
                                  : overdue 
                                    ? 'border-red-300 bg-red-50 cursor-pointer hover:border-red-400' 
                                    : dueSoon 
                                      ? 'border-yellow-300 bg-yellow-50 cursor-pointer hover:border-yellow-400' 
                                      : 'border-gray-200 cursor-pointer hover:border-gray-300'
                            }`}
                            onClick={() => !isDisabled && handlePaymentScheduleToggle(schedule.id)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <div className="flex-shrink-0">
                                  {isPaid ? (
                                    <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                                      <CheckIcon className="h-3 w-3 text-white" />
                                    </div>
                                  ) : (
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => handlePaymentScheduleToggle(schedule.id)}
                                      className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                      disabled={isDisabled}
                                    />
                                  )}
                                </div>
                                <div>
                                  <div className="flex items-center space-x-2">
                                    <p className={`text-sm font-medium ${
                                      isDisabled ? 'text-gray-500' : 'text-gray-900'
                                    }`}>
                                      {schedule.description}
                                    </p>
                                    {isPaid && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                        <CheckIcon className="h-3 w-3 mr-1" />
                                        Ödənilib
                                      </span>
                                    )}
                                    {!isPaid && overdue && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                                        Gecikmiş
                                      </span>
                                    )}
                                    {!isPaid && dueSoon && !overdue && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                        <ClockIcon className="h-3 w-3 mr-1" />
                                        Tezliklə
                                      </span>
                                    )}
                                  </div>
                                  <p className={`text-xs ${
                                    isDisabled ? 'text-gray-400' : 'text-gray-500'
                                  }`}>
                                    Son tarix: {new Date(schedule.due_date).toLocaleDateString('az-AZ')}
                                    {isPaid && (
                                      <span className="ml-2 text-green-600 font-medium">
                                        • Ödənilmiş: {parseFloat(schedule.paid_amount).toLocaleString()} {schedule.currency}
                                      </span>
                                    )}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className={`text-sm font-semibold ${
                                  isDisabled ? 'text-gray-500' : 'text-gray-900'
                                }`}>
                                  {parseFloat(schedule.amount).toLocaleString()} {schedule.currency}
                                </p>
                                {isPaid && (
                                  <p className="text-xs text-green-600 font-medium">
                                    Tam ödənilib
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      </div>
                    )}

                    {selectedPaymentScheduleIds.length > 0 && (
                      <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-blue-900">
                            Seçilmiş ödənişlər: {selectedPaymentScheduleIds.length}
                          </span>
                          <span className="text-lg font-bold text-blue-900">
                            Cəmi: {calculateTotalAmount().toLocaleString()} AZN
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Amount - Auto calculated if payment schedules selected */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Məbləğ *
                  </label>
                  <input
                    type="number"
                    value={selectedPaymentScheduleIds.length > 0 ? calculateTotalAmount() : formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    className={`block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                      selectedPaymentScheduleIds.length > 0 ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                    required
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    disabled={selectedPaymentScheduleIds.length > 0}
                  />
                  {selectedPaymentScheduleIds.length > 0 && (
                    <p className="text-xs text-blue-600 mt-1 font-medium">
                      Məbləğ seçilmiş ödəniş cədvəllərinə görə avtomatik hesablanıb və dəyişdirilə bilməz
                    </p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Təsvir *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={3}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                    placeholder={selectedPaymentScheduleIds.length > 0 ? 
                      `Tələbə ödənişi - ${selectedContract?.contract_number || 'Müqavilə'}` : 
                      "Əməliyyatın təsviri..."
                    }
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Qeydlər
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    rows={2}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Əlavə qeydlər..."
                  />
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Ləğv Et
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Əlavə Et
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Transaction Modal */}
      {isViewModalOpen && viewingTransaction && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Əməliyyat Məlumatları
                </h3>
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Əməliyyat Nömrəsi</label>
                    <p className="mt-1 text-sm text-gray-900">{viewingTransaction.transaction_number}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Əməliyyat Növü</label>
                    <div className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        viewingTransaction.category_type === 'income' 
                          ? 'bg-green-100 text-green-800 border border-green-200' 
                          : 'bg-red-100 text-red-800 border border-red-200'
                      }`}>
                        {viewingTransaction.category_type === 'income' ? (
                          <ArrowTrendingUpIcon className="h-3 w-3 mr-1" />
                        ) : (
                          <ArrowTrendingDownIcon className="h-3 w-3 mr-1" />
                        )}
                        {viewingTransaction.category_type === 'income' ? 'Gəlir' : 'Xərc'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Kateqoriya</label>
                    <div className="mt-1 flex items-center space-x-2">
                      {viewingTransaction.transaction_type?.color && (
                        <div 
                          className="w-4 h-4 rounded-full border border-gray-300" 
                          style={{backgroundColor: viewingTransaction.transaction_type.color}}
                        ></div>
                      )}
                      <p className="text-sm text-gray-900">{viewingTransaction.transaction_type?.name || 'N/A'}</p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Məbləğ</label>
                    <p className={`mt-1 text-sm font-semibold ${
                      viewingTransaction.category_type === 'income' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {viewingTransaction.category_type === 'income' ? '+' : '-'}{parseFloat(viewingTransaction.amount || 0).toLocaleString()} {getCurrencyName(viewingTransaction.currency_id)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Ödəniş Üsulu</label>
                    <p className="mt-1 text-sm text-gray-900">{getPaymentMethodName(viewingTransaction.payment_method_id)}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Tarix</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(viewingTransaction.date)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Vaxt</label>
                    <p className="mt-1 text-sm text-gray-900">{viewingTransaction.time || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Konsultant</label>
                     <p className="mt-1 text-sm text-gray-900">{
                       viewingTransaction.user?.name || 
                       viewingTransaction.consultant?.admin?.name || 
                       getConsultantName(viewingTransaction.processed_by) || 
                       getConsultantName(viewingTransaction.consultant) || 
                       'Sistem'
                     }</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Referans</label>
                    <p className="mt-1 text-sm text-gray-900">{viewingTransaction.reference || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Müqavilə</label>
                    <p className="mt-1 text-sm text-gray-900">{getContractNumber(viewingTransaction)}</p>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-500">Təsvir</label>
                  <p className="mt-1 text-sm text-gray-900">{viewingTransaction.description}</p>
                </div>
                {viewingTransaction.notes && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-500">Qeydlər</label>
                    <p className="mt-1 text-sm text-gray-900">{viewingTransaction.notes}</p>
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div className="flex justify-end space-x-3 pt-6 border-t mt-6">
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Bağla
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Management Modal */}
      {isCategoryModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Əməliyyat Kateqoriyaları
                </h3>
                <button
                  onClick={() => setIsCategoryModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Category Tabs */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => setCategoryTab('income')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      categoryTab === 'income'
                        ? 'border-green-500 text-green-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Gəlir Kateqoriyaları ({categoriesLoading ? '...' : transactionCategories.filter(c => c.type === 'income').length})
                  </button>
                  <button
                    onClick={() => setCategoryTab('expense')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      categoryTab === 'expense'
                        ? 'border-red-500 text-red-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Xərc Kateqoriyaları ({categoriesLoading ? '...' : transactionCategories.filter(c => c.type === 'expense').length})
                  </button>
                </nav>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Category List */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-md font-medium text-gray-900">
                      {categoryTab === 'income' ? 'Gəlir' : 'Xərc'} Kateqoriyaları
                    </h4>
                    <button
                      onClick={handleAddCategory}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <PlusIcon className="h-4 w-4 mr-1" />
                      Yeni
                    </button>
                  </div>

                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {categoriesLoading ? (
                      <div className="text-center py-8">
                        <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm shadow rounded-md text-gray-500 bg-white">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Yüklənir...
                        </div>
                      </div>
                    ) : transactionCategories.filter(cat => cat.type === categoryTab).length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        {categoryTab === 'income' ? 'Gəlir' : 'Xərc'} kateqoriyası tapılmadı
                      </div>
                    ) : (
                      transactionCategories.filter(cat => cat.type === categoryTab).map((category) => (
                        <div key={category.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200" style={{borderLeft: `4px solid ${category.color || '#3B82F6'}`}}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                              <div className="flex items-center space-x-2">
                            <h5 className="text-sm font-medium text-gray-900">{category.name}</h5>
                                <div 
                                  className="w-4 h-4 rounded-full border border-gray-300" 
                                  style={{backgroundColor: category.color || '#3B82F6'}}
                                  title={`Rəng: ${category.color || '#3B82F6'}`}
                                ></div>
                              </div>
                          </div>
                          <div className="flex space-x-2 ml-4">
                            <button
                              onClick={() => handleEditCategory(category)}
                              className="inline-flex items-center p-1.5 bg-blue-100 text-blue-600 hover:bg-blue-200 rounded"
                              title="Redaktə et"
                            >
                              <PencilIcon className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => handleDeleteCategory(category)}
                              className="inline-flex items-center p-1.5 bg-red-100 text-red-600 hover:bg-red-200 rounded"
                              title="Sil"
                            >
                              <TrashIcon className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Category Form */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-4">
                    {editingCategory ? 'Kateqoriyanı Redaktə Et' : 'Yeni Kateqoriya Əlavə Et'}
                  </h4>

                  <form onSubmit={handleCategorySubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Kateqoriya Adı *
                      </label>
                      <input
                        type="text"
                        value={categoryFormData.name}
                        onChange={(e) => setCategoryFormData({...categoryFormData, name: e.target.value})}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        required
                        placeholder="Kateqoriya adını daxil edin..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rəng
                      </label>
                      <div className="flex items-center space-x-3">
                        <input
                          type="color"
                          value={categoryFormData.color}
                          onChange={(e) => setCategoryFormData({...categoryFormData, color: e.target.value})}
                          className="w-16 h-10 border border-gray-300 rounded-md cursor-pointer"
                        />
                        <input
                          type="text"
                          value={categoryFormData.color}
                          onChange={(e) => setCategoryFormData({...categoryFormData, color: e.target.value})}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="#3B82F6"
                          pattern="^#[0-9A-Fa-f]{6}$"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Kateqoriya üçün rəng seçin</p>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingCategory(null);
                          setCategoryFormData({
                            name: '',
                            description: '',
                            type: categoryTab
                          });
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Təmizlə
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                      >
                        {editingCategory ? 'Yenilə' : 'Əlavə Et'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end pt-6 border-t mt-6">
                <button
                  onClick={() => setIsCategoryModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Bağla
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Method Management Modal */}
      {isPaymentMethodModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Ödəniş Üsulları İdarəsi
                </h3>
                <button
                  onClick={() => setIsPaymentMethodModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Payment Methods List */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-md font-medium text-gray-900">
                      Ödəniş Üsulları ({paymentMethods.length})
                    </h4>
                    <button
                      onClick={handleAddPaymentMethod}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <PlusIcon className="h-4 w-4 mr-1" />
                      Yeni
                    </button>
                  </div>

                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {paymentMethods.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        Ödəniş üsulu tapılmadı
                      </div>
                    ) : (
                      paymentMethods.map((paymentMethod) => (
                        <div key={paymentMethod.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <h5 className="text-sm font-medium text-gray-900">{paymentMethod.name}</h5>
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                  {paymentMethod.code}
                                </span>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  paymentMethod.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                  {paymentMethod.is_active !== false ? 'Aktiv' : 'Deaktiv'}
                                </span>
                              </div>
                              {paymentMethod.description && (
                                <p className="text-xs text-gray-500 mt-1">{paymentMethod.description}</p>
                              )}
                            </div>
                            <div className="flex space-x-2 ml-4">
                              <button
                                onClick={() => handleEditPaymentMethod(paymentMethod)}
                                className="inline-flex items-center p-1.5 bg-blue-100 text-blue-600 hover:bg-blue-200 rounded"
                                title="Redaktə et"
                              >
                                <PencilIcon className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => handleDeletePaymentMethod(paymentMethod)}
                                className="inline-flex items-center p-1.5 bg-red-100 text-red-600 hover:bg-red-200 rounded"
                                title="Sil"
                              >
                                <TrashIcon className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Payment Method Form */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-4">
                    {editingPaymentMethod ? 'Ödəniş Üsulunu Redaktə Et' : 'Yeni Ödəniş Üsulu Əlavə Et'}
                  </h4>

                  <form onSubmit={handlePaymentMethodSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Adı *
                      </label>
                      <input
                        type="text"
                        value={paymentMethodFormData.name}
                        onChange={(e) => setPaymentMethodFormData({...paymentMethodFormData, name: e.target.value})}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        required
                        maxLength={100}
                        placeholder="Ödəniş üsulu adını daxil edin..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Kod *
                      </label>
                      <input
                        type="text"
                        value={paymentMethodFormData.code}
                        onChange={(e) => setPaymentMethodFormData({...paymentMethodFormData, code: e.target.value.toUpperCase()})}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        required
                        maxLength={20}
                        pattern="^[A-Z_]+$"
                        placeholder="BANK_TRANSFER"
                        title="Yalnız böyük hərflər və alt xətt (_) istifadə edin"
                      />
                      <p className="text-xs text-gray-500 mt-1">Yalnız böyük hərflər və alt xətt (_) istifadə edin</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Təsvir
                      </label>
                      <textarea
                        value={paymentMethodFormData.description}
                        onChange={(e) => setPaymentMethodFormData({...paymentMethodFormData, description: e.target.value})}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        rows={3}
                        maxLength={500}
                        placeholder="Ödəniş üsulu haqqında əlavə məlumat..."
                      />
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={paymentMethodFormData.is_active}
                        onChange={(e) => setPaymentMethodFormData({...paymentMethodFormData, is_active: e.target.checked})}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                        Aktiv
                      </label>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingPaymentMethod(null);
                          setPaymentMethodFormData({
                            name: '',
                            code: '',
                            description: '',
                            is_active: true
                          });
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Təmizlə
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                      >
                        {editingPaymentMethod ? 'Yenilə' : 'Əlavə Et'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end pt-6 border-t mt-6">
                <button
                  onClick={() => setIsPaymentMethodModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Bağla
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 